<template>
  <div class="v3-widget v3-panel" v-testid="testid">
    <div class="v3-panel-head">
      <h3 v-testid="testid + '-title'">{{ title }}</h3>
      <span v-if="subtitle" class="sub">{{ subtitle }}</span>
    </div>

    <div class="v3-prog-body">
      <div class="v3-prog-head">
        <span class="v3-prog-total">{{ ready }}%</span>
        <span class="v3-prog-unit">audit-ready</span>
        <span v-if="openCount" class="v3-prog-insight">{{ openCount }} need attention</span>
      </div>
      <div class="v3-prog-bar" role="img" :aria-label="ready + '% audit ready'">
        <span class="v3-prog-seg" :style="{ width: ready + '%', background: 'var(--success-fg)' }"></span>
        <span class="v3-prog-seg" :style="{ width: (100 - ready) + '%', background: '#9AA4B2' }"></span>
      </div>
    </div>

    <div v-if="gaps.length" class="v3-list">
      <div v-for="(g, i) in gaps" :key="i" class="v3-row v3-row--2" v-testid="testid + '-row-' + g.label">
        <span class="v3-anom">
          <span class="v3-anom-ic v3-anom-ic--med"><span class="ms">{{ g.icon || 'warning' }}</span></span>
          <span class="v3-anom-main"><span class="v3-row-title">{{ g.label }}</span></span>
        </span>
        <span class="v3-audit-n">{{ g.count }}</span>
      </div>
    </div>

    <button v-if="linkText && linkAllowed" class="v3-prog-link" type="button" v-testid="testid + '-audit-log'" @click="onLink">
      {{ linkText }}<span class="ms">chevron_right</span>
    </button>
  </div>
</template>

<script>
import axios from "axios";
import { auditReadiness } from "@rm-ui/homepage/auditReadiness";
import { canNavigateTo } from "@/composables/useV3Nav";

// New-portal (v3) Audit — the audit-readiness panel (prototype V3Audit):
// ready% bar + gap rows + "Open audit log" link. Live by default via the shared
// `auditReadiness` composable (POST Report/auditReadiness); dataSource:'static'
// falls back to authored ready/gaps. Sibling: rm-portal-beta AuditWidget.vue.
export default {
  name: "cLandingV3Audit",
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const ar = auditReadiness({
      http: axios,
      getEnabled: () => props.definition.dataSource !== "static",
    });
    return { ar };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3Audit";
    },
    isStatic() {
      return this.definition.dataSource === "static";
    },
    title() {
      return this.definition.title || "Audit readiness";
    },
    subtitle() {
      return this.definition.subtitle === undefined ? "EU Pay Transparency" : this.definition.subtitle;
    },
    ready() {
      const v = this.isStatic ? this.definition.ready : this.ar.ready.value;
      return Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
    },
    gaps() {
      if (this.isStatic) return Array.isArray(this.definition.gaps) ? this.definition.gaps : [];
      return this.ar.gaps.value;
    },
    openCount() {
      return this.gaps.reduce((s, g) => s + (Number(g.count) || 0), 0);
    },
    linkText() {
      return this.definition.linkText === undefined ? "Open audit log" : this.definition.linkText;
    },
    // Footer link is permission-gated (legacy landing-config parity); the
    // editor shows it regardless.
    linkAllowed() {
      const dest = this.definition.linkUrl || "";
      return this.editMode || !dest || canNavigateTo(this, dest);
    },
  },
  methods: {
    onLink() {
      if (this.editMode) return;
      const dest = this.definition.linkUrl;
      if (!dest) return;
      if (/^https?:\/\//i.test(dest)) {
        window.open(dest, "_blank");
        return;
      }
      this.$router.push({ name: dest }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
